import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CreditCard, Check, AlertCircle, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

export function BankDetailsForm({ clubId, onComplete, showTitle = true }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState(false);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_name: '',
    sort_code: '',
    account_number: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (clubId) {
      fetchBankDetails();
    }
  }, [clubId]);

  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/clubs/${clubId}/bank-details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHasBankDetails(data.has_bank_details);
        if (data.bank_details) {
          setFormData({
            bank_name: data.bank_details.bank_name || '',
            account_name: data.bank_details.account_name || '',
            sort_code: data.bank_details.sort_code || '',
            account_number: data.bank_details.account_number || ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching bank details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatSortCode = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
  };

  const handleSortCodeChange = (e) => {
    const formatted = formatSortCode(e.target.value);
    setFormData(prev => ({ ...prev, sort_code: formatted }));
    if (errors.sort_code) setErrors(prev => ({ ...prev, sort_code: null }));
  };

  const handleAccountNumberChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    setFormData(prev => ({ ...prev, account_number: digits }));
    if (errors.account_number) setErrors(prev => ({ ...prev, account_number: null }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.bank_name.trim()) {
      newErrors.bank_name = 'Bank name is required';
    }
    
    if (!formData.account_name.trim()) {
      newErrors.account_name = 'Account name is required';
    }
    
    const sortCodeDigits = formData.sort_code.replace(/\D/g, '');
    if (sortCodeDigits.length !== 6) {
      newErrors.sort_code = 'Sort code must be 6 digits';
    }
    
    if (formData.account_number.length !== 8) {
      newErrors.account_number = 'Account number must be 8 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/clubs/${clubId}/bank-details`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Bank details saved successfully');
        setHasBankDetails(true);
        if (onComplete) onComplete();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save bank details');
      }
    } catch (error) {
      console.error('Error saving bank details:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showTitle && (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Payout Details</h3>
            <p className="text-sm text-slate-500">
              {hasBankDetails ? 'Your bank details are saved' : 'Add your bank details to receive payouts'}
            </p>
          </div>
          {hasBankDetails && (
            <Check className="w-5 h-5 text-emerald-500 ml-auto" />
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="bank_name" className="text-slate-700">Bank Name</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="bank_name"
                placeholder="e.g., Lloyds Bank"
                value={formData.bank_name}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, bank_name: e.target.value }));
                  if (errors.bank_name) setErrors(prev => ({ ...prev, bank_name: null }));
                }}
                className={`pl-10 ${errors.bank_name ? 'border-red-500' : ''}`}
              />
            </div>
            {errors.bank_name && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.bank_name}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="account_name" className="text-slate-700">Account Name</Label>
            <Input
              id="account_name"
              placeholder="Name on the account"
              value={formData.account_name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, account_name: e.target.value }));
                if (errors.account_name) setErrors(prev => ({ ...prev, account_name: null }));
              }}
              className={errors.account_name ? 'border-red-500' : ''}
            />
            {errors.account_name && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.account_name}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="sort_code" className="text-slate-700">Sort Code</Label>
            <Input
              id="sort_code"
              placeholder="XX-XX-XX"
              value={formData.sort_code}
              onChange={handleSortCodeChange}
              className={`font-mono ${errors.sort_code ? 'border-red-500' : ''}`}
              maxLength={8}
            />
            {errors.sort_code && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.sort_code}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="account_number" className="text-slate-700">Account Number</Label>
            <Input
              id="account_number"
              placeholder="8 digits"
              value={formData.account_number}
              onChange={handleAccountNumberChange}
              className={`font-mono ${errors.account_number ? 'border-red-500' : ''}`}
              maxLength={8}
            />
            {errors.account_number && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.account_number}
              </p>
            )}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 inline-block mr-2" />
          Your bank details are securely stored and only used to transfer ticket sale proceeds to you.
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="min-w-[120px]">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {hasBankDetails ? 'Update Details' : 'Save Details'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default BankDetailsForm;
